import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputBase,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { downloadAuthorizationLetter, getAuthorizationLetter } from "../../utils/API";
import { showToast } from "../../utils/tostr";

/**
 * Reusable inline editable field that looks like plain text until focused.
 * Behaves like a lightweight "text editor" cell inside the letter.
 */
const EditableField = ({
    value,
    onChange,
    placeholder,
    multiline = false,
    width,
    bold = false,
    align = "left",
    minWidth = 120,
}) => (
    <InputBase
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        multiline={multiline}
        fullWidth={multiline}
        sx={{
            width: width,
            minWidth: multiline ? "100%" : minWidth,
            fontSize: 14,
            fontFamily: '"Times New Roman", serif',
            fontWeight: bold ? 700 : 400,
            lineHeight: 1.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            border: "1px dashed transparent",
            transition: "all 0.15s ease",
            "& input, & textarea": { textAlign: align, p: 0 },
            "&:hover": { borderColor: "#cfd8dc", backgroundColor: "#fafafa" },
            "&.Mui-focused": {
                borderColor: "#1976d2",
                backgroundColor: "#f3f8ff",
                borderStyle: "solid",
            },
        }}
    />
);

const LabelRow = ({ label, children }) => (
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5, flexWrap: "wrap" }}>
        <Typography
            sx={{
                fontFamily: '"Times New Roman", serif',
                fontSize: 14,
                fontWeight: 600,
                minWidth: 170,
            }}
        >
            {label}
        </Typography>
        {children}
    </Box>
);

const emptyDeductionRow = () => ({
    id: `${Math.floor(performance.now() * 1000)}-${Math.round(Math.random() * 1e6)}`,
    disallowedAmount: "",
    reason: "",
    description: "",
});

// Formats a numeric amount for display in the letter (2 decimals, Indian grouping).
const fmtAmount = (n) =>
    (Math.round((Number(n) || 0) * 100) / 100).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

/**
 * Derives the Deduction Details amounts from the PML waterfall
 * (claimData.tariffPmlDataMerged.billTariffPmlAmountList), summed across every
 * bill line item. The waterfall runs:
 *   Tariff → Amount After Room Proportional Discount → Amount After Mou Discount
 *   → Amount Before Copay → Final Amount → Final Amount Sent To Jarvis (drop).
 * Each deduction is the reduction between two consecutive stages:
 *   MOU Discount        = Amount After Room Proportional Discount − Amount After Mou Discount
 *   Sublimit (Limit)    = Amount After Mou Discount             − Amount Before Copay
 *   Co-pay              = Amount Before Copay                   − Final Amount
 *   Sum insured exhausted = Amount Approved (Σ Final Amount)    − Final Amount Sent To Jarvis
 */
const computeDeductionTotals = (claimData) => {
    const categories = claimData?.tariffPmlDataMerged?.billTariffPmlAmountList || [];
    let mouDiscount = 0;
    let sublimit = 0;
    let copay = 0;
    let amountApproved = 0; // Σ final_amount
    let finalSentToJarvis = 0; // Σ final_amount_after_drop

    categories.forEach((cat) => {
        (cat?.billItemList || []).forEach((item) => {
            const afterRoom = Number(item?.amount_after_room_proportional_discount) || 0;
            const afterMou = Number(item?.amount_after_mou_discount) || 0;
            const beforeCopay = Number(item?.amountBeforeCopay) || 0;
            const finalAmt = Number(item?.final_amount) || 0;
            const afterDrop = Number(item?.final_amount_after_drop) || 0;

            mouDiscount += afterRoom - afterMou;
            sublimit += afterMou - beforeCopay;
            copay += beforeCopay - finalAmt;
            amountApproved += finalAmt;
            finalSentToJarvis += afterDrop;
        });
    });

    return {
        mouDiscount,
        sublimit,
        copay,
        siExhausted: amountApproved - finalSentToJarvis,
        amountApproved,
        finalSentToJarvis,
    };
};

// Maps the letter's row labels back to their editable field keys. Used to
// hydrate the editor from a previously saved letter's HTML.
const LABEL_TO_FIELD = {
    "Hospital Name :": "hospitalName",
    "Pre-auth Reference no :": "preAuthRefNo",
    "Primary Insured Name :": "primaryInsuredName",
    "Patient Name :": "patientName",
    "Policy Number :": "policyNumber",
    "Date of issue :": "dateOfIssue",
    "DOA :": "doa",
    "DOD :": "dod",
    "Valid till :": "validTill",
    "Claim amount :": "claimAmount",
    "Disallowed :": "disallowed",
    "Approved amount :": "approvedAmount",
};

/**
 * Parses the saved letter HTML (as produced by buildLetterHtml) back into the
 * editor's { fields, deductions } shape so it can be rendered & edited.
 * Returns null if parsing fails.
 */
const parseLetterHtml = (html) => {
    try {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const clean = (el) =>
            el ? (el.textContent || "").replace(/ /g, " ").trim() : "";

        const fields = {};

        // Two-column label/value rows across both detail tables.
        doc.querySelectorAll("tr").forEach((tr) => {
            const tds = tr.querySelectorAll("td");
            if (tds.length === 2) {
                const key = LABEL_TO_FIELD[clean(tds[0])];
                if (key) fields[key] = clean(tds[1]);
            }
        });

        // Date (top-right block: "Date: DD-MM-YYYY").
        doc.querySelectorAll("div").forEach((d) => {
            const b = d.querySelector("b");
            if (b && clean(b).startsWith("Date:") && d.children.length === 1) {
                fields.date = clean(d).replace(/^Date:\s*/, "");
            }
        });

        // Subject line (fully editable text).
        const subjEl = doc.querySelector('[data-field="subject"]');
        if (subjEl) {
            fields.subject = clean(subjEl);
        } else {
            // Backward-compat: reconstruct from the old split subject markup.
            doc.querySelectorAll("p").forEach((p) => {
                const span = p.querySelector("span");
                if (span && clean(span).includes("Authorization of Cashless")) {
                    fields.subject = clean(p);
                }
            });
        }

        // Intro paragraph (fully editable text).
        const introEl = doc.querySelector('[data-field="intro"]');
        if (introEl) fields.intro = clean(introEl);

        // Comments block.
        doc.querySelectorAll("p").forEach((p) => {
            const b = p.querySelector("b");
            if (b && clean(b).startsWith("Comments:")) {
                fields.comments = clean(p).replace(/^Comments:\s*/, "");
            }
        });

        // Deduction table (the bordered one with the known headers).
        const dedTable = Array.from(doc.querySelectorAll("table")).find((t) =>
            (t.querySelector("thead th")?.textContent || "").includes(
                "Disallowed amount"
            )
        );
        const deductions = [];
        if (dedTable) {
            dedTable.querySelectorAll("tbody tr").forEach((tr) => {
                const tds = tr.querySelectorAll("td");
                if (tds.length >= 3) {
                    deductions.push({
                        ...emptyDeductionRow(),
                        disallowedAmount: clean(tds[0]),
                        reason: clean(tds[1]),
                        description: clean(tds[2]),
                    });
                }
            });
        }

        return { fields, deductions };
    } catch (e) {
        return null;
    }
};

const AuthorizationLetter = ({ claimData = {}, claimId, onDownload }) => {
    const printRef = useRef(null);

    const buildInitialState = () => ({
        hospitalName: claimData?.hospitalName || "",
        preAuthRefNo: claimData?.intimationNumber || "",
        primaryInsuredName: claimData?.primaryInsuredName || claimData?.patientName || "",
        patientName: claimData?.patientName || "",
        policyNumber: claimData?.policyNumber || "",
        date: moment().format("DD-MM-YYYY"),
        subject: "Subject: Authorization of Cashless for Rs.0.00 as per the final bill .",
        intro:
            "The afore mentioned claim is being admitted in your hospital, kindly extend the cashless facility as per below details:",
        dateOfIssue: moment().format("DD-MM-YYYY"),
        doa: claimData?.dateOfAdmission
            ? moment(claimData.dateOfAdmission).format("DD-MM-YYYY")
            : "",
        dod: claimData?.dateOfDischarge
            ? moment(claimData.dateOfDischarge).format("DD-MM-YYYY")
            : "",
        validTill: "",
        claimAmount: "",
        disallowed: "",
        approvedAmount: "",
        comments:
            "Eligible for Any Room. AL approved surgical management. Kindly submit all investigation reports along with supporting documents at the time of Claim submission. Claim will be processed as per agreed tariff with Hospital on receipt of all claim documents. Kindly collect NMEs from patient.",
    });

    const [fields, setFields] = useState(buildInitialState);

    // Deduction amounts derived from the PML waterfall for this claim.
    const buildInitialDeductions = () => {
        const totals = computeDeductionTotals(claimData);
        return [
            {
                ...emptyDeductionRow(),
                disallowedAmount: fmtAmount(totals.mouDiscount),
                reason: "MOU Discount",
                description:
                    "10% discount on SOC excluding Medicine, Consumable, Stents, Implant, Outsource test, Blood & Blood Products & Packages",
            },
            {
                ...emptyDeductionRow(),
                reason: "Tariff Deduction",
                description: "Tariff Deduction (Do not collect from patient)",
            },
            {
                ...emptyDeductionRow(),
                reason: "NMEs",
                description: "Amount to be paid by insured",
            },
            {
                ...emptyDeductionRow(),
                disallowedAmount: fmtAmount(totals.copay),
                reason: "Co-pay",
                description: "Amount to be paid by insured",
            },
            {
                ...emptyDeductionRow(),
                disallowedAmount: fmtAmount(totals.sublimit),
                reason: "Sublimit",
                description: "Amount to be paid by insured",
            },
            {
                ...emptyDeductionRow(),
                disallowedAmount: fmtAmount(totals.siExhausted),
                reason: "Sum insured exhausted",
                description: "Amount to be paid by insured",
            },
        ];
    };

    const [deductions, setDeductions] = useState(buildInitialDeductions);

    // Tracks whether the user has manually edited the letter after it loaded.
    // Drives the `isEditing` flag sent to the download api.
    const [isEditing, setIsEditing] = useState(false);
    // Raw HTML returned by the GET api (null when HTML_NOT_FOUND / not saved).
    // Reused verbatim on download when the user hasn't edited anything.
    const [savedHtml, setSavedHtml] = useState(null);
    // Contextual note shown above the letter.
    const [letterNote, setLetterNote] = useState("");

    const setField = (key) => (val) => {
        setIsEditing(true);
        setFields((p) => ({ ...p, [key]: val }));
    };

    const updateDeduction = (id, key, val) => {
        setIsEditing(true);
        setDeductions((rows) =>
            rows.map((r) => (r.id === id ? { ...r, [key]: val } : r))
        );
    };

    const addDeductionRow = () => {
        setIsEditing(true);
        setDeductions((rows) => [...rows, emptyDeductionRow()]);
    };

    const removeDeductionRow = (id) => {
        setIsEditing(true);
        setDeductions((rows) => rows.filter((r) => r.id !== id));
    };

    // ---- Actions menu state ----
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [exporting, setExporting] = useState(null); // 'pdf' | 'docx' | 'png' | null
    const [loadingLetter, setLoadingLetter] = useState(true);
    const menuOpen = Boolean(menuAnchor);
    const openMenu = (e) => setMenuAnchor(e.currentTarget);
    const closeMenu = () => setMenuAnchor(null);

    const resolvedClaimId = claimId || claimData?.id;

    // On open, fetch any previously saved letter for this claim and hydrate the
    // editor from it. Falls back to the claim-derived defaults if none exists.
    useEffect(() => {
        let cancelled = false;
        const loadExisting = async () => {
            if (!resolvedClaimId && resolvedClaimId !== 0) {
                setLoadingLetter(false);
                return;
            }
            setLoadingLetter(true);
            try {
                const res = await getAuthorizationLetter(resolvedClaimId);
                const html = res?.data?.html;
                if (!cancelled && res?.success && html) {
                    // A saved letter exists. The note depends on the response's
                    // `edited` flag: true => edited by ABHI Team, false => this is
                    // still the untouched first response.
                    setSavedHtml(html);
                    setIsEditing(false);
                    setLetterNote(
                        res?.data?.edited === true
                            ? "As per the editing done by ABHI Team"
                            : `As per the first response shared on ${moment().format("DD-MM-YYYY")}`
                    );
                    const parsed = parseLetterHtml(html);
                    if (parsed) {
                        setFields((prev) => ({ ...prev, ...parsed.fields }));
                        if (parsed.deductions.length) setDeductions(parsed.deductions);
                    }
                } else if (!cancelled) {
                    // HTML_NOT_FOUND — no saved letter, show the first response draft.
                    setSavedHtml(null);
                    setIsEditing(false);
                    setLetterNote(
                        `As per the first response shared on ${moment().format("DD-MM-YYYY")}`
                    );
                }
            } catch (e) {
                // No saved letter (or fetch failed) — keep the default draft.
                if (!cancelled) {
                    setSavedHtml(null);
                    setIsEditing(false);
                    setLetterNote(
                        `As per the first response shared on ${moment().format("DD-MM-YYYY")}`
                    );
                }
            } finally {
                if (!cancelled) setLoadingLetter(false);
            }
        };
        loadExisting();
        return () => {
            cancelled = true;
        };
    }, [resolvedClaimId]);

    const escapeHtml = (str = "") =>
        String(str)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\n/g, "<br/>");

    // Static (non-editable) HTML rendering of the letter, reused for preview & all exports.
    const buildLetterHtml = () => {
        const row = (label, value) => `
            <tr>
                <td style="font-weight:600;white-space:nowrap;padding:2px 8px 2px 0;vertical-align:top;">${label}</td>
                <td style="padding:2px 0;">${escapeHtml(value) || "&nbsp;"}</td>
            </tr>`;

        const deductionRows = deductions
            .map(
                (d) => `
            <tr>
                <td style="border:1px solid #000;padding:6px 8px;vertical-align:top;">${escapeHtml(d.disallowedAmount) || "&nbsp;"}</td>
                <td style="border:1px solid #000;padding:6px 8px;vertical-align:top;">${escapeHtml(d.reason) || "&nbsp;"}</td>
                <td style="border:1px solid #000;padding:6px 8px;vertical-align:top;">${escapeHtml(d.description) || "&nbsp;"}</td>
            </tr>`
            )
            .join("");

        return `
        <div style="font-family:'Times New Roman',serif;color:#000;font-size:14px;line-height:1.5;background:#fff;padding:40px;width:720px;box-sizing:border-box;">
            <div style="text-align:right;margin-bottom:16px;"><b>Date:</b> ${escapeHtml(fields.date)}</div>
            <table style="border-collapse:collapse;">
                ${row("Hospital Name :", fields.hospitalName)}
                ${row("Pre-auth Reference no :", fields.preAuthRefNo)}
                ${row("Primary Insured Name :", fields.primaryInsuredName)}
                ${row("Patient Name :", fields.patientName)}
                ${row("Policy Number :", fields.policyNumber)}
            </table>
            <p data-field="subject" style="text-align:center;font-weight:700;font-size:15px;margin:24px 0;text-decoration:underline;">${escapeHtml(fields.subject)}</p>
            <p data-field="intro">${escapeHtml(fields.intro)}</p>
            <table style="border-collapse:collapse;">
                ${row("Date of issue :", fields.dateOfIssue)}
                ${row("DOA :", fields.doa)}
                ${row("DOD :", fields.dod)}
                ${row("Valid till :", fields.validTill)}
                ${row("Claim amount :", fields.claimAmount)}
                ${row("Disallowed :", fields.disallowed)}
                ${row("Approved amount :", fields.approvedAmount)}
            </table>
            <p style="margin-top:24px;"><b>Comments:</b><br/>${escapeHtml(fields.comments)}</p>
            <p style="font-weight:700;margin-bottom:4px;">Deduction Details:</p>
            <table style="border-collapse:collapse;width:100%;border:1px solid #000;">
                <thead>
                    <tr>
                        <th style="border:1px solid #000;padding:6px 8px;text-align:left;width:22%;">Disallowed amount</th>
                        <th style="border:1px solid #000;padding:6px 8px;text-align:left;width:28%;">Reason</th>
                        <th style="border:1px solid #000;padding:6px 8px;text-align:left;">Description</th>
                    </tr>
                </thead>
                <tbody>${deductionRows}</tbody>
            </table>
        </div>`;
    };

    // Download formats are generated on the backend: we POST the rendered letter
    // HTML + the desired downloadType, and the backend streams back the file.
    const requestDownload = async (format) => {
        // When the user has manually edited the letter, send the freshly rendered
        // HTML with isEditing:true. Otherwise reuse the saved HTML from the GET api
        // as-is (falling back to the default draft when nothing was saved) and
        // flag isEditing:false so the backend keeps the original response.
        const edited = isEditing;
        const html = edited ? buildLetterHtml() : savedHtml || buildLetterHtml();

        // Allow the parent to fully override the download behaviour if it wants to.
        if (typeof onDownload === "function") {
            onDownload({ format, fields, deductions, html, claimId: resolvedClaimId, isEditing: edited });
            return;
        }

        setExporting(format);
        try {
            await downloadAuthorizationLetter({
                html,
                downloadType: format,
                claimId: resolvedClaimId,
                edited,
                fileName: `Authorization_Letter_${(fields.preAuthRefNo || resolvedClaimId || "letter")
                    .toString()
                    .replace(/[^\w-]+/g, "_")}`,
            });
            showToast(`Authorization letter downloaded as ${format.toUpperCase()}`, "success", "auth-letter-download");
        } catch (e) {
            showToast(
                e?.message
                    ? `Failed to download authorization letter (${format.toUpperCase()}): ${e.message}`
                    : `Failed to download authorization letter (${format.toUpperCase()})`,
                "error",
                "auth-letter-download-error"
            );
        } finally {
            setExporting(null);
        }
    };

    const handleAction = (action) => {
        closeMenu();
        if (action === "preview") setPreviewOpen(true);
        else requestDownload(action); // 'pdf' | 'docx' | 'png'
    };

    const cellSx = { border: "1px solid #000", p: 1, verticalAlign: "top" };

    return (
        <Paper elevation={2} sx={{ maxWidth: 900, mx: "auto", my: 3, borderRadius: 2 }}>
            {/* Toolbar */}
            <Stack
                direction="row"
                spacing={1}
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: "1px solid #e0e0e0",
                    bgcolor: "#f5f7fa",
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                }}
            >
                <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#37474f" }}>
                    Authorization Letter
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="contained"
                        endIcon={
                            exporting ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <KeyboardArrowDownIcon />
                            )
                        }
                        onClick={openMenu}
                        disabled={Boolean(exporting) || loadingLetter}
                        aria-haspopup="true"
                        aria-expanded={menuOpen ? "true" : undefined}
                    >
                        {exporting ? `Generating ${exporting.toUpperCase()}…` : "Actions"}
                    </Button>
                    <Menu
                        anchorEl={menuAnchor}
                        open={menuOpen}
                        onClose={closeMenu}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                        <MenuItem onClick={() => handleAction("preview")}>
                            <ListItemIcon>
                                <VisibilityOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Preview Authorization Letter</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => handleAction("pdf")}>
                            <ListItemIcon>
                                <PictureAsPdfOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Download as PDF (.pdf)</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleAction("docx")}>
                            <ListItemIcon>
                                <DescriptionOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Download as Word (.docx)</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => handleAction("png")}>
                            <ListItemIcon>
                                <ImageOutlinedIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Download as PNG (.png)</ListItemText>
                        </MenuItem>
                    </Menu>
                </Stack>
            </Stack>

            {/* Contextual note: reflects whether an ABHI-Team edited letter was
                loaded, or this is the first-response draft. */}
            {!loadingLetter && letterNote && (
                <Box
                    sx={{
                        px: { xs: 3, md: 6 },
                        py: 1,
                        bgcolor: "#fff8e1",
                        borderBottom: "1px solid #ffe082",
                    }}
                >
                    <Typography
                        sx={{ fontSize: 13, color: "#8d6e63", fontStyle: "italic" }}
                    >
                        Note: {letterNote}
                    </Typography>
                </Box>
            )}

            {loadingLetter ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        py: 10,
                        bgcolor: "#fff",
                    }}
                >
                    <CircularProgress size={28} />
                    <Typography sx={{ fontSize: 14, color: "#78909c" }}>
                        Loading authorization letter…
                    </Typography>
                </Box>
            ) : (
            /* Document body */
            <Box
                ref={printRef}
                sx={{
                    p: { xs: 3, md: 6 },
                    bgcolor: "#fff",
                    fontFamily: '"Times New Roman", serif',
                }}
            >
                {/* Date top right */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Date:</Typography>
                        <EditableField
                            value={fields.date}
                            onChange={setField("date")}
                            placeholder="DD-MM-YYYY"
                            width={140}
                        />
                    </Box>
                </Box>

                {/* Header details */}
                <LabelRow label="Hospital Name :">
                    <EditableField
                        value={fields.hospitalName}
                        onChange={setField("hospitalName")}
                        placeholder="Enter hospital name"
                        width={320}
                    />
                </LabelRow>
                <LabelRow label="Pre-auth Reference no :">
                    <EditableField
                        value={fields.preAuthRefNo}
                        onChange={setField("preAuthRefNo")}
                        placeholder="Enter reference no"
                        width={320}
                    />
                </LabelRow>
                <LabelRow label="Primary Insured Name :">
                    <EditableField
                        value={fields.primaryInsuredName}
                        onChange={setField("primaryInsuredName")}
                        placeholder="Enter primary insured name"
                        width={320}
                    />
                </LabelRow>
                <LabelRow label="Patient Name :">
                    <EditableField
                        value={fields.patientName}
                        onChange={setField("patientName")}
                        placeholder="Enter patient name"
                        width={320}
                    />
                </LabelRow>
                <LabelRow label="Policy Number :">
                    <EditableField
                        value={fields.policyNumber}
                        onChange={setField("policyNumber")}
                        placeholder="Enter policy number"
                        width={320}
                    />
                </LabelRow>

                {/* Subject */}
                <Box sx={{ my: 3 }}>
                    <EditableField
                        value={fields.subject}
                        onChange={setField("subject")}
                        placeholder="Enter subject"
                        bold
                        align="center"
                        multiline
                    />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <EditableField
                        value={fields.intro}
                        onChange={setField("intro")}
                        placeholder="Enter introductory text"
                        multiline
                    />
                </Box>

                {/* Claim details */}
                <LabelRow label="Date of issue :">
                    <EditableField
                        value={fields.dateOfIssue}
                        onChange={setField("dateOfIssue")}
                        placeholder="DD-MM-YYYY"
                        width={180}
                    />
                </LabelRow>
                <LabelRow label="DOA :">
                    <EditableField
                        value={fields.doa}
                        onChange={setField("doa")}
                        placeholder="DD-MM-YYYY"
                        width={180}
                    />
                </LabelRow>
                <LabelRow label="DOD :">
                    <EditableField
                        value={fields.dod}
                        onChange={setField("dod")}
                        placeholder="DD-MM-YYYY"
                        width={180}
                    />
                </LabelRow>
                <LabelRow label="Valid till :">
                    <EditableField
                        value={fields.validTill}
                        onChange={setField("validTill")}
                        placeholder="DD-MM-YYYY"
                        width={180}
                    />
                </LabelRow>
                <LabelRow label="Claim amount :">
                    <EditableField
                        value={fields.claimAmount}
                        onChange={setField("claimAmount")}
                        placeholder="0.00"
                        width={180}
                    />
                </LabelRow>
                <LabelRow label="Disallowed :">
                    <EditableField
                        value={fields.disallowed}
                        onChange={setField("disallowed")}
                        placeholder="0.00"
                        width={180}
                    />
                </LabelRow>
                <LabelRow label="Approved amount :">
                    <EditableField
                        value={fields.approvedAmount}
                        onChange={setField("approvedAmount")}
                        placeholder="0.00"
                        width={180}
                    />
                </LabelRow>

                {/* Comments */}
                <Box sx={{ mt: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>
                        Comments:
                    </Typography>
                    <EditableField
                        value={fields.comments}
                        onChange={setField("comments")}
                        placeholder="Enter comments"
                        multiline
                    />
                </Box>

                {/* Deduction Details */}
                <Box
                    sx={{
                        mt: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        Deduction Details:
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addDeductionRow}
                        className="no-print"
                    >
                        Add Row
                    </Button>
                </Box>

                <TableContainer sx={{ mt: 1 }}>
                    <Table size="small" sx={{ border: "1px solid #000" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...cellSx, fontWeight: 700, width: "22%" }}>
                                    Disallowed amount
                                </TableCell>
                                <TableCell sx={{ ...cellSx, fontWeight: 700, width: "28%" }}>
                                    Reason
                                </TableCell>
                                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                                    Description
                                </TableCell>
                                <TableCell
                                    sx={{ ...cellSx, fontWeight: 700, width: 56 }}
                                    align="center"
                                    className="no-print"
                                >
                                    {""}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {deductions.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell sx={cellSx}>
                                        <EditableField
                                            value={row.disallowedAmount}
                                            onChange={(v) =>
                                                updateDeduction(row.id, "disallowedAmount", v)
                                            }
                                            placeholder="0.00"
                                            minWidth={80}
                                        />
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <EditableField
                                            value={row.reason}
                                            onChange={(v) => updateDeduction(row.id, "reason", v)}
                                            placeholder="Enter reason"
                                            multiline
                                        />
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        <EditableField
                                            value={row.description}
                                            onChange={(v) =>
                                                updateDeduction(row.id, "description", v)
                                            }
                                            placeholder="Enter description"
                                            multiline
                                        />
                                    </TableCell>
                                    <TableCell sx={cellSx} align="center" className="no-print">
                                        <Tooltip title="Remove row">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeDeductionRow(row.id)}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {deductions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} sx={cellSx} align="center">
                                        <Typography sx={{ fontSize: 13, color: "#90a4ae" }}>
                                            No deductions added. Click "Add Row" to add one.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Divider sx={{ mt: 4, mb: 1 }} />
                <Typography sx={{ fontSize: 12, color: "#78909c", fontStyle: "italic" }}>
                    This is a system generated letter. All fields above are editable — click
                    any field to modify its content.
                </Typography>
            </Box>
            )}

            {/* Preview dialog — read-only rendering of the current letter */}
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    Preview — Authorization Letter
                    <IconButton onClick={() => setPreviewOpen(false)} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: "#eceff1" }}>
                    <Box
                        sx={{
                            mx: "auto",
                            bgcolor: "#fff",
                            boxShadow: 2,
                            width: "fit-content",
                            maxWidth: "100%",
                            overflowX: "auto",
                        }}
                        dangerouslySetInnerHTML={{ __html: buildLetterHtml() }}
                    />
                </DialogContent>
            </Dialog>
        </Paper>
    );
};

export default AuthorizationLetter;
